import { getPathAlias } from 'tw-oi-core/utils/vehicle'

import { ROUTE, DIVISIONS, UI, PRODUCT_MAP } from '../config'
import { clearLastVehicle } from './cookies'
import { isOffline } from '../services/offlineDetect'

/**
 * Returns the division config for a vehicle product type
 *
 * @param {string} productType FOREST_RIVER_PRODUCT_TYPE value
 * @returns {object|null}
 */
export function getDivisionForProductType(productType) {
  if (!productType) return null

  return DIVISIONS.find(division => division.productTypes.includes(productType)) || null
}

/**
 * Builds the division vehicles route for the current model context
 *
 * @param {object} options
 * @param {string} options.productType FOREST_RIVER_PRODUCT_TYPE value
 * @param {string} options.year model year
 * @returns {string} router path
 */
export function buildDivisionVehiclesRoute({ productType, year }) {
  const division = getDivisionForProductType(productType)
  if (!division || !year) return ROUTE.INDEX

  const group = getPathAlias(productType)
  return `${ROUTE.DIVISIONS}/${division.key}${ROUTE.VEHICLES}/${year}/${group}`
}

/**
 * Navigates from the unified header logo
 *
 * @param {object} history react-router history
 * @param {boolean} [offline] whether the browser is offline
 */
export function navigateUnifiedHeaderLogo(history, offline = isOffline()) {
  if (offline) {
    history.push(ROUTE.OFFLINE)
    return
  }

  clearLastVehicle()
  history.push(ROUTE.INDEX)
}

/**
 * Builds the top-menu "Select Model" link.
 *
 * @param {object} options
 * @param {object} options.userMedia user media flags from Redux
 * @param {boolean} [options.offline] whether the browser is offline
 * @param {string} [options.productType] current model product type
 * @param {string} [options.year] current model year
 * @returns {object} TopMenu link descriptor
 */
export function buildSelectModelMenuLink({
  userMedia,
  offline = isOffline(),
  productType,
  year,
}) {
  const route = offline
    ? ROUTE.OFFLINE
    : buildDivisionVehiclesRoute({ productType, year })

  return {
    url: `#${route}`,
    title: userMedia.isDesktop ? UI.MENU_TITLE_SELECT_MODEL.desktop : UI.MENU_TITLE_SELECT_MODEL.mobile,
    onMenuClick: () => clearLastVehicle(),
  }
}

/**
 * Builds mobile dropdown navigation for division vehicle screens.
 *
 * @param {object} options
 * @param {object} options.division division config
 * @param {string} [options.productType] current product type key
 * @param {string} [options.year] active year
 * @param {string} options.baseRoute division base route
 * @param {function} [options.onSelectCategoryClick] handler when selecting category
 * @returns {object|null}
 */
export function buildDivisionMobileDropdown({
  division,
  productType,
  year,
  baseRoute,
  onSelectCategoryClick,
}) {
  if (!division) return null

  const divisionPath = `${baseRoute}${ROUTE.VEHICLES}/${year || ''}`
  const items = [
    {
      label: UI.BREADCRUMB_SELECT_CATEGORY,
      to: ROUTE.INDEX,
      onClick: onSelectCategoryClick,
    },
    {
      label: division.title,
      ...(productType ? { to: divisionPath } : { isCurrent: true }),
    },
  ]

  if (productType && PRODUCT_MAP[productType]) {
    items.push({
      label: PRODUCT_MAP[productType].title,
      isCurrent: true,
    })
  }

  return {
    label: productType && PRODUCT_MAP[productType]
      ? PRODUCT_MAP[productType].title
      : division.title,
    items,
  }
}

/**
 * Builds mobile dropdown for static single-label headers (e.g. Welcome).
 *
 * @param {string} label
 * @returns {object}
 */
export function buildStaticMobileDropdown(label) {
  return {
    label,
    items: [{ label, isCurrent: true }],
  }
}

/**
 * Builds mobile actions tray for browse guide header (SEND FEEDBACK, CONTACT US, Select Model).
 *
 * @param {object} options
 * @param {object} options.userMedia user media flags from Redux
 * @param {boolean} [options.offline] whether the browser is offline
 * @param {string} [options.productType] current model product type
 * @param {string} [options.year] current model year
 * @param {function} options.onFeedbackClick handler when opening feedback modal
 * @returns {object|null}
 */
export function buildBrowseGuideMobileActionsTray({
  userMedia,
  offline = isOffline(),
  productType,
  year,
  onFeedbackClick,
}) {
  const selectModelLink = buildSelectModelMenuLink({
    userMedia,
    offline,
    productType,
    year,
  })
  const items = []

  if (!offline) {
    items.push(
      {
        label: 'SEND FEEDBACK',
        onClick: onFeedbackClick,
      },
      {
        label: 'CONTACT US',
        href: 'https://www.forestriverinc.com/Contact-Us',
        target: '_blank',
        rel: 'noreferrer',
      },
    )
  }

  items.push({
    label: UI.MENU_TITLE_SELECT_MODEL.desktop,
    href: selectModelLink.url,
    onClick: selectModelLink.onMenuClick,
  })

  return {
    label: 'Menu',
    items,
    triggerVariant: 'dots',
    reverseItems: false,
    showTrayHeader: false,
    className: 'DropdownOpenTray--actionsTray',
  }
}

/**
 * Builds mobile dropdown for browse guide vehicle title breadcrumb.
 *
 * @param {object} options
 * @param {string} options.currentModel
 * @param {string} options.currentYear
 * @param {string} options.brandName
 * @returns {object|null}
 */
export function buildBrowseGuideMobileDropdown({ currentModel, currentYear, brandName }) {
  if (!currentModel) return null

  return {
    label: currentModel,
    items: [
      {
        label: `${currentYear} ${brandName}`.trim(),
        isLabelOnly: true,
      },
      {
        label: currentModel,
        isCurrent: true,
      },
    ],
  }
}
